import { AsyncThunkAction, createAsyncThunk } from '@reduxjs/toolkit'
import {
  AverageActionsForRelatedControlsResponse,
  AverageCostForRelatedControls,
  AverageSCScore,
  CountDoneActionsForRelatedControlsResponse,
  CountDoneActionsForSecurityControlResponse,
  CountInProgressActionsForSecurityControlResponse,
  CountOtherActionsForRelatedControlsResponse,
  CountOtherActionsForSecurityControlResponse,
  GlobalAverageActionsData,
  InProgressActionsForRelatedControlsResponse,
  LateRateForRelatedControls,
  RelatedControlsActionsCosts,
  RelatedControlsCostsResponse,
  RelatedControlsTree,
  SecurityControlData,
  securityControlResponse,
  SecurityControlResponse,
  SecurityControls,
} from '../../types/securityControls'
import {
  calculateAverageActionsForRelatedControls,
  calculateAverageActionsForSecurityControlApi,
  calculateAverageCostForRelatedControls,
  calculateAverageCostForSecurityControlApi,
  calculateGlobalAverageActionsPerSecurityControl,
  calculateLateRateForRelatedControls,
  calculateRelatedControlsActionsCosts,
  calculateRelatedControlsCosts,
  calculateSecurityControlActionsCostsApi,
  countDoneActionsForRelatedControls,
  countDoneActionsForSecurityControl,
  countInProgressActionsForRelatedControls,
  countInProgressActionsForSecurityControl,
  countOtherActionsForRelatedControls,
  countOtherActionsForSecurityControl,
  getAllSecurityControls,
  getAuditsFromRelatedControls,
  getAverageSCScoreFromRelatedControls,
  getRelatedControlsTreeByFamilyId,
  getSecurityControlByIdApi,
  getSecurityControlByLabelApi,
  getSecurityControlsBySource,
  getSecurityISOApi,
  getSecurityNISTApi,
  getwithSCScores,
  updateSecurityCintrolsApi,
} from '../../api/securityControlsApi'
import { SecurityCheckResponse } from '../../types/securityCheckFamilysTypes'
import { Audit } from '../../types/auditTypes'
import { handleError } from '../../utils/errorHandler'

export const getAllSecurity = createAsyncThunk<
  {
    securityControls: SecurityControls[]
    total?: number
    totalPages?: number
    status?: number
    erreur?: boolean
    message?: string
  },
  { page?: number; pageSize?: number },
  { rejectValue: string }
>('security/getAllSecurity', async ({ page, pageSize }, thunkAPI) => {
  try {
    const response = await getAllSecurityControls({ page, pageSize })
    return {
      securityControls: response.securityControl || [],
      total: response.total,
      totalPages: response.totalPages,
      status: response.status,
      message: response.message,
      erreur: response.erreur,
    }
  } catch (error) {
    return thunkAPI.rejectWithValue(await handleError(error))
  }
})
export const fetchAuditsFromRelatedControls = createAsyncThunk<
  Audit[],
  string,
  { rejectValue: string }
>('securityControls/fetchAuditsFromRelatedControls', async (id, thunkAPI) => {
  try {
    const response = await getAuditsFromRelatedControls(id)
    return response
  } catch (error) {
    return thunkAPI.rejectWithValue(
      'Failed to fetch audits from related controls.'
    )
  }
})
export const fetchAverageSCScoreFromRelatedControls = createAsyncThunk<
  AverageSCScore,
  string,
  { rejectValue: string }
>('securityControls/fetchAverageSCScore', async (id, thunkAPI) => {
  try {
    const response = await getAverageSCScoreFromRelatedControls(id)
    return response
  } catch (error) {
    return thunkAPI.rejectWithValue(
      'Failed to fetch average SCScore from related controls.'
    )
  }
})
export const fetchAverageActionsForRelatedControls = createAsyncThunk<
  AverageActionsForRelatedControlsResponse,
  string,
  { rejectValue: string }
>(
  'securityControls/fetchAverageActionsForRelatedControls',
  async (id, thunkAPI) => {
    try {
      const response = await calculateAverageActionsForRelatedControls(id)
      return response
    } catch (error) {
      return thunkAPI.rejectWithValue(
        'Failed to fetch average actions for related controls.'
      )
    }
  }
)
export const fetchAverageCostForRelatedControls = createAsyncThunk<
  AverageCostForRelatedControls,
  string,
  { rejectValue: string }
>(
  'securityControls/fetchAverageCostForRelatedControls',
  async (id, thunkAPI) => {
    try {
      const response = await calculateAverageCostForRelatedControls(id)
      return response
    } catch (error) {
      return thunkAPI.rejectWithValue(
        'Failed to fetch average cost for related controls.'
      )
    }
  }
)

export const fetchRelatedControlsActionsCosts = createAsyncThunk<
  RelatedControlsActionsCosts,
  string,
  { rejectValue: string }
>('securityControls/fetchRelatedControlsActionsCosts', async (id, thunkAPI) => {
  try {
    const response = await calculateRelatedControlsActionsCosts(id)
    return response
  } catch (error) {
    return thunkAPI.rejectWithValue(
      'Failed to fetch total cost for related controls actions.'
    )
  }
})
export const fetchRelatedControlsCosts = createAsyncThunk<
  RelatedControlsCostsResponse,
  string,
  { rejectValue: string }
>('securityControls/fetchRelatedControlsCosts', async (id, thunkAPI) => {
  try {
    const response = await calculateRelatedControlsCosts(id)
    return response
  } catch (error) {
    return thunkAPI.rejectWithValue(await handleError(error))
  }
})
export const fetchInProgressActionsForRelatedControls = createAsyncThunk<
  InProgressActionsForRelatedControlsResponse,
  string,
  { rejectValue: string }
>(
  'securityControls/fetchInProgressActionsForRelatedControls',
  async (id, thunkAPI) => {
    try {
      const response = await countInProgressActionsForRelatedControls(id)
      return response
    } catch (error) {
      return thunkAPI.rejectWithValue(
        'Failed to fetch in-progress actions for related controls.'
      )
    }
  }
)
export const fetchRelatedControlsTreeByFamilyId = createAsyncThunk<
  RelatedControlsTree,
  string,
  { rejectValue: string }
>(
  'securityCheckFamily/fetchRelatedControlsTreeByFamilyId',
  async (id, thunkAPI) => {
    try {
      const response = await getRelatedControlsTreeByFamilyId(id)
      return response
    } catch (error) {
      return thunkAPI.rejectWithValue(await handleError(error))
    }
  }
)
export const getSecurityControlById = createAsyncThunk<
  SecurityControls,
  string,
  { rejectValue: string }
>('security/getSecurityControlById', async (id, thunkAPI) => {
  try {
    const response = await getSecurityControlByIdApi(id)
    return response
  } catch (error) {
    return thunkAPI.rejectWithValue(await handleError(error))
  }
})

export const getSecurityISO = createAsyncThunk<
  { securityControls: SecurityControls[]; total: number; totalPages: number },
  { page?: number; pageSize?: number; language?: string },
  { rejectValue: string }
>('security/getAllSecurityISO', async ({ page, pageSize, language }, thunkAPI) => {
  try {
    const response = await getSecurityISOApi({ page, pageSize, language })
    return {
      securityControls: response.securityControls || [],
      total: response.total,
      totalPages: response.totalPages,
    }
  } catch (error) {
    return thunkAPI.rejectWithValue(await handleError(error))
  }
})

export const getSecurityNIST = createAsyncThunk<
  { securityControls: SecurityControls[]; total: number; totalPages: number },
  { page?: number; pageSize?: number; language?: string },
  { rejectValue: string }
>('security/getAllSecurityNIST', async ({ page, pageSize, language }, thunkAPI) => {
  try {
    const response = await getSecurityNISTApi({ page, pageSize, language })
    return {
      securityControls: response.securityControls || [],
      total: response.total,
      totalPages: response.totalPages,
    }
  } catch (error) {
    return thunkAPI.rejectWithValue(await handleError(error))
  }
})
export const fetchGlobalAverageActionsPerSecurityControl = createAsyncThunk<
  GlobalAverageActionsData,
  void,
  { rejectValue: string }
>('statistics/fetchGlobalAverage', async (_, thunkAPI) => {
  try {
    const response = await calculateGlobalAverageActionsPerSecurityControl()
    return response.data
  } catch (error) {
    return thunkAPI.rejectWithValue(await handleError(error))
  }
})
export const fetchLateRateForRelatedControls = createAsyncThunk<
  LateRateForRelatedControls,
  string,
  { rejectValue: string }
>('securityControls/fetchLateRateForRelatedControls', async (id, thunkAPI) => {
  try {
    const response = await calculateLateRateForRelatedControls(id)
    return response
  } catch (error) {
    return thunkAPI.rejectWithValue(
      'Failed to fetch late rate for related controls.'
    )
  }
})
export const updateSecuritycontrols = createAsyncThunk<
  SecurityControls,
  { updatedSecurityControl: SecurityControls; _id: string },
  { rejectValue: string }
>(
  'subDomain/updateSubDomain',
  async ({ updatedSecurityControl, _id }, thunkAPI) => {
    try {
      const response = await updateSecurityCintrolsApi(
        updatedSecurityControl,
        _id
      )
      return response
    } catch (error) {
      return thunkAPI.rejectWithValue(await handleError(error))
    }
  }
)
export const getSecurityControlsWithScores = createAsyncThunk<
  SecurityCheckResponse,
  string | undefined,
  { rejectValue: string }
>('security/getSecurityControlsWithScores', async (language, thunkAPI) => {
  try {
    const response = await getwithSCScores(language)
    console.log('response', response)
    return response
  } catch (error) {
    return thunkAPI.rejectWithValue(
      'Failed to get Security Controls with Scores'
    )
  }
})
export const calculateSecurityControlActionsCosts = createAsyncThunk<
  SecurityCheckResponse,
  string,
  { rejectValue: string }
>(
  'security/calculateSecurityCheckFamilyActionsCosts',
  async (SecurityCheckId, thunkAPI) => {
    try {
      const response =
        await calculateSecurityControlActionsCostsApi(SecurityCheckId)
      console.log(response)
      return response
    } catch (error) {
      return thunkAPI.rejectWithValue(
        'Failed to calculate Security  CheckFamilyActions Costs'
      )
    }
  }
)
export const calculateAverageActionsForSecurityControl = createAsyncThunk<
  SecurityCheckResponse,
  string,
  { rejectValue: string }
>(
  'security/calculateAverageActionsForSecurityControl',
  async (SecurityCheckId, thunkAPI) => {
    try {
      const response =
        await calculateAverageActionsForSecurityControlApi(SecurityCheckId)
      console.log(response)
      return response
    } catch (error) {
      return thunkAPI.rejectWithValue(
        'Failed to calculate Security  CheckFamilyActions Costs'
      )
    }
  }
)
export const calculateAverageCostForSecurityControl = createAsyncThunk<
  SecurityControlData,
  string,
  { rejectValue: string }
>('securityControl/calculateAverageCost', async (id, thunkAPI) => {
  try {
    const response = await calculateAverageCostForSecurityControlApi(id)
    return response.data
  } catch (error) {
    return thunkAPI.rejectWithValue(
      'Failed to calculate average cost for the security control.'
    )
  }
})
export const createFetchSecurityControlsBySource = (
  source: 'NIST' | 'NIS2' | 'SAMA' | 'SWIFT'
) =>
  createAsyncThunk<
    { source: string; response: securityControlResponse },
    { page?: number; pageSize?: number; language?: string },
    { rejectValue: string }
  >(
    `securityControls/fetchBy${source}Source`,
    async ({ page = 1, pageSize = 10, language }, thunkAPI) => {
      try {
        const response = await getSecurityControlsBySource(
          source,
          page,
          pageSize,
          language
        )
        return { source, response }
      } catch (error) {
        return thunkAPI.rejectWithValue(
          `Failed to fetch security controls by ${source} source.`
        )
      }
    }
  )
export const fetchSecurityControlsByNIS2 =
  createFetchSecurityControlsBySource('NIS2')
export const fetchSecurityControlsBySAMA =
  createFetchSecurityControlsBySource('SAMA')
export const fetchSecurityControlsBySWIFT =
  createFetchSecurityControlsBySource('SWIFT')

export const fetchSecurityControlsByLabels = createAsyncThunk<
  SecurityControls[],
  string[],
  { rejectValue: string }
>('securityControls/fetchByLabels', async (labels, thunkAPI) => {
  try {
    const promises = labels.map((label) => getSecurityControlByLabelApi(label))
    const results = await Promise.allSettled(promises)

    const securityControls = results
      .filter((result) => result.status === 'fulfilled')
      .map((result: any) => result.value)
      .filter((control) => control != null)

    return securityControls
  } catch (error) {
    return thunkAPI.rejectWithValue(
      'Failed to fetch security controls by labels.'
    )
  }
})

export const fetchSecurityControlsByCategory = ({
  category,
  page,
  pageSize,
  language,
}: {
  category: 'ISO' | 'NIST' | 'NIS2' | 'SAMA' | 'SWIFT'
  page?: number
  pageSize?: number
  language?: string
}): AsyncThunkAction<any, any, any> => {
  switch (category) {
    case 'ISO':
      return getSecurityISO({ page: page ?? 1, pageSize: pageSize ?? 10, language })
    case 'NIST':
      return getSecurityNIST({ page: page ?? 1, pageSize: pageSize ?? 10, language })
    case 'NIS2':
      return fetchSecurityControlsByNIS2({
        page: page ?? 1,
        pageSize: pageSize ?? 10,
        language,
      })
    case 'SAMA':
      return fetchSecurityControlsBySAMA({
        page: page ?? 1,
        pageSize: pageSize ?? 10,
        language,
      })
    case 'SWIFT':
      return fetchSecurityControlsBySWIFT({
        page: page ?? 1,
        pageSize: pageSize ?? 10,
        language,
      })
  }
}

// New Redux actions for the count APIs
export const fetchCountOtherActionsForSecurityControl = createAsyncThunk<
  CountOtherActionsForSecurityControlResponse,
  string,
  { rejectValue: string }
>(
  'securityControls/fetchCountOtherActionsForSecurityControl',
  async (id, thunkAPI) => {
    try {
      const response = await countOtherActionsForSecurityControl(id)
      return response
    } catch (error) {
      return thunkAPI.rejectWithValue(
        'Failed to count other actions for security control.'
      )
    }
  }
)

export const fetchCountOtherActionsForRelatedControls = createAsyncThunk<
  CountOtherActionsForRelatedControlsResponse,
  string,
  { rejectValue: string }
>(
  'securityControls/fetchCountOtherActionsForRelatedControls',
  async (id, thunkAPI) => {
    try {
      const response = await countOtherActionsForRelatedControls(id)
      return response
    } catch (error) {
      return thunkAPI.rejectWithValue(
        'Failed to count other actions for related controls.'
      )
    }
  }
)

export const fetchCountInProgressActionsForSecurityControl = createAsyncThunk<
  CountInProgressActionsForSecurityControlResponse,
  string,
  { rejectValue: string }
>(
  'securityControls/fetchCountInProgressActionsForSecurityControl',
  async (id, thunkAPI) => {
    try {
      const response = await countInProgressActionsForSecurityControl(id)
      return response
    } catch (error) {
      return thunkAPI.rejectWithValue(
        'Failed to count in-progress actions for security control.'
      )
    }
  }
)

export const fetchCountDoneActionsForSecurityControl = createAsyncThunk<
  CountDoneActionsForSecurityControlResponse,
  string,
  { rejectValue: string }
>(
  'securityControls/fetchCountDoneActionsForSecurityControl',
  async (id, thunkAPI) => {
    try {
      const response = await countDoneActionsForSecurityControl(id)
      return response
    } catch (error) {
      return thunkAPI.rejectWithValue(
        'Failed to count done actions for security control.'
      )
    }
  }
)

export const fetchCountDoneActionsForRelatedControls = createAsyncThunk<
  CountDoneActionsForRelatedControlsResponse,
  string,
  { rejectValue: string }
>(
  'securityControls/fetchCountDoneActionsForRelatedControls',
  async (id, thunkAPI) => {
    try {
      const response = await countDoneActionsForRelatedControls(id)
      return response
    } catch (error) {
      return thunkAPI.rejectWithValue(
        'Failed to count done actions for related controls.'
      )
    }
  }
)
